import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DetailsRequestsView } from '@/components/DetailsRequestsTabs';

/** The query param a record section reads its open view from. */
export const DETAILS_REQUESTS_VIEW_PARAM = 'view';

/**
 * A record section's Details/Requests view, kept in the URL so a link — a
 * change-request notification, say — can open the section on its Requests
 * tab. Anything other than `requests` reads as Details.
 */
export const useDetailsRequestsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const view: DetailsRequestsView =
    searchParams.get(DETAILS_REQUESTS_VIEW_PARAM) === 'requests'
      ? 'requests'
      : 'details';

  // Stable like a state setter, so callers can list it as a hook dependency.
  const setView = useCallback(
    (next: DetailsRequestsView) =>
      setSearchParams(
        (params) => {
          const updated = new URLSearchParams(params);
          if (next === 'requests')
            updated.set(DETAILS_REQUESTS_VIEW_PARAM, next);
          else updated.delete(DETAILS_REQUESTS_VIEW_PARAM);
          return updated;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  return [view, setView] as const;
};
